import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/fd4472baf2d825262c34fc49a57bdbbb.jpg"
          alt="Hero Background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/35 to-[#0d0d0d]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
      </div>

      {/* Ambient glow orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full hero-orb-gold" />
        <div className="absolute top-1/3 right-1/4 w-56 h-56 rounded-full hero-orb-silver" style={{ animationDelay: '2.2s' }} />
        <div className="absolute bottom-1/3 left-1/2 w-40 h-40 rounded-full hero-orb-gold" style={{ animationDelay: '1.1s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-5 sm:px-6 w-full">

        {/* Logo */}
        <div
          className={`mb-5 sm:mb-6 transition-all duration-1000 delay-100 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
          style={{ filter: 'drop-shadow(0 0 18px rgba(212,168,67,0.7)) drop-shadow(0 0 40px rgba(212,168,67,0.35))' }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/c79b8da1-7d89-4197-bfec-80ebde63b2cd.png"
            alt="重燃：靈魂共鳴賽 Logo"
            className="w-18 h-18 sm:w-24 sm:h-24 object-contain hero-logo-float"
            style={{ width: '4.5rem', height: '4.5rem' }}
          />
        </div>

        {/* Decorative line + badge */}
        <div
          className={`flex items-center gap-2 sm:gap-4 mb-4 transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <div className="h-px w-5 sm:w-14 bg-gradient-to-r from-transparent to-[#d4a843]/70" />
          <span className="text-[#d4a843] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.4em] uppercase whitespace-nowrap font-cinzel">
            Soul Resonance Championship
          </span>
          <div className="h-px w-5 sm:w-14 bg-gradient-to-l from-transparent to-[#d4a843]/70" />
        </div>

        {/* Main Title */}
        <div
          className={`transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <h1
            className="font-black text-white leading-none tracking-tight mb-1 font-serif"
            style={{ fontSize: 'clamp(3.5rem, 15vw, 7rem)' }}
          >
            重<span
              style={{
                color: '#d4a843',
                textShadow: '0 0 25px rgba(212,168,67,0.9), 0 0 55px rgba(212,168,67,0.5), 0 0 90px rgba(212,168,67,0.25)',
              }}
            >燃</span>
          </h1>
          <h2
            className="font-bold text-[#e8e8e8] mb-4 sm:mb-5 font-serif"
            style={{ fontSize: 'clamp(1.1rem, 5vw, 1.875rem)', letterSpacing: '0.35em' }}
          >
            靈魂共鳴賽
          </h2>
        </div>

        {/* Tagline */}
        <p
          className={`text-[#d0d0d0] text-sm sm:text-base max-w-[18rem] sm:max-w-xl mb-1 leading-relaxed transition-all duration-1000 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}
        >
          這不是出遊，是一場<strong className="text-[#d4a843]" style={{ textShadow: '0 0 12px rgba(212,168,67,0.5)' }}>為友誼而戰</strong>的冒險
        </p>

        {/* Ornament divider */}
        <div
          className={`flex items-center gap-2 sm:gap-3 my-5 sm:my-8 transition-all duration-1000 delay-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="h-px w-8 sm:w-16 bg-gradient-to-r from-transparent to-[#d4a843]/50" />
          <div className="flex gap-1.5 items-center">
            <div className="w-1 h-1 rounded-full bg-[#d4a843]/40" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#d4a843]/70" />
            <div className="w-1 h-1 rounded-full bg-[#d4a843]/40" />
          </div>
          <div className="h-px w-8 sm:w-16 bg-gradient-to-l from-transparent to-[#d4a843]/50" />
        </div>

        {/* VS Battle */}
        <div
          className={`relative flex items-center justify-center w-full max-w-[18rem] sm:max-w-sm mx-auto transition-all duration-1000 delay-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Black Team */}
          <div
            className="flex flex-col items-center gap-2 sm:gap-3 flex-1 cursor-pointer group"
            onClick={() => {
              const el = document.getElementById('members');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full transition-all duration-300 group-hover:opacity-150"
                style={{ boxShadow: '0 0 35px 10px rgba(212,168,67,0.35), 0 0 70px 18px rgba(212,168,67,0.15)' }}
              />
              <div
                className="absolute -inset-2 rounded-full border border-[#d4a843]/30 animate-ping"
                style={{ animationDuration: '2.5s' }}
              />
              <div
                className="relative w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-2 transition-all duration-300 group-hover:scale-110 group-hover:border-[#f0c84e]"
                style={{
                  background: 'radial-gradient(circle at 35% 35%, #2a2010, #0d0d0d)',
                  borderColor: '#d4a843',
                  boxShadow: 'inset 0 0 18px rgba(212,168,67,0.2)',
                }}
              >
                <i className="ri-sword-fill text-xl sm:text-3xl" style={{ color: '#d4a843', filter: 'drop-shadow(0 0 8px #d4a843aa)' }} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-white font-black text-sm sm:text-base tracking-widest group-hover:text-[#d4a843] transition-colors duration-200">黑隊</div>
              <div className="text-[#d4a843] text-[10px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] uppercase mt-0.5 font-cinzel">Black Team</div>
              <div className="text-[#d4a843]/50 text-[10px] tracking-widest mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">點擊查看 →</div>
            </div>
          </div>

          {/* VS */}
          <div className="flex flex-col items-center gap-1 px-1 sm:px-2 shrink-0">
            <div className="w-px h-5 sm:h-8 bg-gradient-to-b from-transparent to-[#d4a843]/60" />
            <div
              className="relative cursor-pointer group"
              onClick={() => {
                const el = document.getElementById('pk');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              <div
                className="absolute inset-0 rounded-full transition-all duration-300"
                style={{ boxShadow: '0 0 20px 6px rgba(212,168,67,0.4)' }}
              />
              <div
                className="relative w-9 h-9 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                style={{
                  background: 'radial-gradient(circle, #1e1508 0%, #0d0d0d 70%)',
                  border: '1px solid rgba(212,168,67,0.5)',
                }}
              >
                <span
                  className="text-sm sm:text-xl font-black tracking-tighter transition-opacity duration-200 group-hover:opacity-0 absolute font-serif"
                  style={{
                    color: '#d4a843',
                    textShadow: '0 0 10px #d4a843, 0 0 20px #d4a84388',
                  }}
                >
                  VS
                </span>
                <i
                  className="ri-arrow-down-line text-sm sm:text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute"
                  style={{ color: '#d4a843', filter: 'drop-shadow(0 0 6px #d4a843)' }}
                />
              </div>
            </div>
            <div className="w-px h-5 sm:h-8 bg-gradient-to-t from-transparent to-[#d4a843]/60" />
          </div>

          {/* White Team */}
          <div
            className="flex flex-col items-center gap-2 sm:gap-3 flex-1 cursor-pointer group"
            onClick={() => {
              const el = document.getElementById('members');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full transition-all duration-300"
                style={{ boxShadow: '0 0 35px 10px rgba(230,230,230,0.28), 0 0 70px 18px rgba(200,200,200,0.12)' }}
              />
              <div
                className="absolute -inset-2 rounded-full border border-white/25 animate-ping"
                style={{ animationDuration: '2.5s', animationDelay: '0.8s' }}
              />
              <div
                className="relative w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center border-2 transition-all duration-300 group-hover:scale-110 group-hover:border-white"
                style={{
                  background: 'radial-gradient(circle at 35% 35%, #2a2a2a, #141414)',
                  borderColor: '#d0d0d0',
                  boxShadow: 'inset 0 0 18px rgba(220,220,220,0.12)',
                }}
              >
                <i className="ri-sword-fill text-xl sm:text-3xl" style={{ color: '#e0e0e0', filter: 'drop-shadow(0 0 8px #ffffffaa)', transform: 'scaleX(-1)' }} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-white font-black text-sm sm:text-base tracking-widest group-hover:text-[#e0e0e0] transition-colors duration-200">白隊</div>
              <div className="text-[#bbb] text-[10px] sm:text-[10px] tracking-[0.2em] sm:tracking-[0.3em] uppercase mt-0.5 font-cinzel">White Team</div>
              <div className="text-white/40 text-[10px] tracking-widest mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">點擊查看 →</div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div
          className={`mt-10 sm:mt-14 transition-all duration-1000 delay-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <p className="text-[#d4a843]/35 text-[10px] sm:text-[10px] tracking-[0.5em] uppercase font-cinzel">Scroll to Explore</p>
        </div>
      </div>

      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-9px); }
        }
        .hero-logo-float {
          animation: heroFloat 4s ease-in-out infinite;
        }
        @keyframes heroOrbPulse {
          0%, 100% { opacity: 0; transform: scale(0.7); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        .hero-orb-gold {
          background: radial-gradient(circle, rgba(212,168,67,0.13) 0%, transparent 70%);
          animation: heroOrbPulse 7s ease-in-out infinite;
        }
        .hero-orb-silver {
          background: radial-gradient(circle, rgba(220,220,220,0.08) 0%, transparent 70%);
          animation: heroOrbPulse 8s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
